import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

/*
 * Invented company wordmarks. Plain type in distinct faces and weights,
 * a few with a tiny geometric symbol. Sized in em, so set the font size on the parent.
 */

export type CompanyName =
    | 'Halyard'
    | 'Northfold'
    | 'Ostrava Labs'
    | 'Pendulum'
    | 'Tessel'
    | 'Brightline'
    | 'Marrow'
    | 'Quillon'
    | 'Fathom'
    | 'Lumen & Co'
    | 'Verso'
    | 'Atlas Row'
    | 'Kiln'

export const COMPANIES: CompanyName[] = [
    'Halyard',
    'Northfold',
    'Ostrava Labs',
    'Pendulum',
    'Tessel',
    'Brightline',
    'Marrow',
    'Quillon',
    'Fathom',
    'Lumen & Co',
    'Verso',
    'Atlas Row',
    'Kiln'
]

interface SymbolProps {
    children: ReactNode
    viewBox?: string
    className?: string
}

const Symbol: FC<SymbolProps> = ({ children, viewBox = '0 0 16 16', className }): ReactNode => {
    return (
        <svg viewBox={viewBox} aria-hidden='true' className={cn('h-[0.9em] w-[0.9em] shrink-0', className)} fill='currentColor'>
            {children}
        </svg>
    )
}

const MARKS: Record<CompanyName, ReactNode> = {
    'Halyard': (
        <span className='inline-flex items-center gap-[0.4em] font-sans text-[0.86em] font-semibold uppercase tracking-[0.2em]'>
            <Symbol>
                <path d='M8 1 L 14 13 L 8 13 Z' />
                <rect x='3' y='14' width='11' height='1.4' />
            </Symbol>
            Halyard
        </span>
    ),
    'Northfold': (
        <span className='inline-flex items-center gap-[0.35em] font-sans text-[1.02em] font-bold lowercase tracking-[-0.05em]'>
            <Symbol>
                <path d='M2 2 H 14 V 14 Z' opacity='0.45' />
                <path d='M2 2 L 14 14 H 2 Z' />
            </Symbol>
            northfold
        </span>
    ),
    'Ostrava Labs': (
        <span className='font-mono text-[0.8em] uppercase tracking-[0.06em]'>
            Ostrava<span className='opacity-50'>/</span>Labs
        </span>
    ),
    'Pendulum': (
        <span className='inline-flex items-center gap-[0.3em] font-serif text-[1.18em] italic tracking-[-0.01em]'>
            <Symbol viewBox='0 0 12 16' className='h-[0.75em] w-[0.6em]'>
                <rect x='5.4' y='0' width='1.2' height='9' />
                <circle cx='6' cy='12' r='3.4' />
            </Symbol>
            Pendulum
        </span>
    ),
    'Tessel': (
        <span className='inline-flex items-center gap-[0.4em] font-sans text-[1.04em] font-black lowercase tracking-[-0.06em]'>
            <Symbol>
                <rect x='1' y='1' width='6' height='6' rx='1' />
                <rect x='9' y='1' width='6' height='6' rx='3' />
                <rect x='1' y='9' width='6' height='6' rx='3' />
                <rect x='9' y='9' width='6' height='6' rx='1' />
            </Symbol>
            tessel
        </span>
    ),
    'Brightline': (
        <span className='relative font-serif text-[1.12em] font-medium tracking-[-0.03em]'>
            Brightline
            <span aria-hidden='true' className='absolute -right-[0.5em] top-[55%] h-[0.08em] w-[0.36em] bg-current' />
        </span>
    ),
    'Marrow': (
        <span className='font-serif text-[1em] uppercase tracking-[0.3em]'>
            Marrow
        </span>
    ),
    'Quillon': (
        <span className='inline-flex items-baseline font-sans text-[1.08em] font-light tracking-[-0.03em]'>
            quillon
            <span aria-hidden='true' className='ml-[0.12em] inline-block size-[0.24em] rounded-full bg-current' />
        </span>
    ),
    'Fathom': (
        <span className='inline-flex items-center gap-[0.4em] font-sans text-[0.94em] font-extrabold uppercase tracking-[0.04em]'>
            <Symbol>
                <rect x='1' y='3' width='14' height='1.6' rx='0.8' />
                <rect x='3' y='7.2' width='10' height='1.6' rx='0.8' />
                <rect x='5.5' y='11.4' width='5' height='1.6' rx='0.8' />
            </Symbol>
            Fathom
        </span>
    ),
    'Lumen & Co': (
        <span className='font-serif text-[1.14em] tracking-[-0.01em]'>
            Lumen <span className='italic'>&amp;</span> Co.
        </span>
    ),
    'Verso': (
        <span className='inline-flex items-center gap-[0.3em] font-mono text-[0.94em] lowercase'>
            <Symbol>
                <path d='M8 2 A 6 6 0 0 0 8 14 Z' />
                <path d='M8 2 A 6 6 0 0 1 8 14' fill='none' stroke='currentColor' strokeWidth='1.2' />
            </Symbol>
            verso
        </span>
    ),
    'Atlas Row': (
        <span className='inline-flex items-center gap-[0.5em] font-sans text-[0.82em] font-semibold uppercase tracking-[0.24em]'>
            Atlas
            <Symbol viewBox='0 0 16 4' className='h-[0.25em] w-[0.9em]'>
                <circle cx='2' cy='2' r='1.6' />
                <circle cx='8' cy='2' r='1.6' />
                <circle cx='14' cy='2' r='1.6' />
            </Symbol>
            Row
        </span>
    ),
    'Kiln': (
        <span className='inline-flex items-center gap-[0.35em] font-serif text-[1.12em] font-semibold tracking-[-0.02em]'>
            <Symbol>
                <path d='M2 15 V 8 A 6 6 0 0 1 14 8 V 15 H 10.5 V 9 A 2.5 2.5 0 0 0 5.5 9 V 15 Z' />
            </Symbol>
            Kiln
        </span>
    )
}

interface WordmarkProps {
    name: CompanyName
    className?: string
}

/** One invented company wordmark. It inherits colour, so tint it with a text class. */
export const Wordmark: FC<WordmarkProps> = ({ name, className }): ReactNode => {
    return (
        <span role='img' aria-label={name} className={cn('inline-flex items-center whitespace-nowrap leading-none', className)}>
            <span aria-hidden='true' className='inline-flex items-center'>
                {MARKS[name]}
            </span>
        </span>
    )
}
